import * as React from 'react';
import { styled } from '@glitz/react';
import Image from 'webapp/components/image';
import { translate, useMatchMobile } from '@avensia/excite';
import {
  createMaxMobileViewportDecorator,
  createMinLargeViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import { Theme } from 'webapp/theme';
import { Button } from 'webapp/components/button';
import { FieldSize } from 'webapp/components/field-size';
import { maxContentNormalWidthDecorator } from './page-layout';
import { ContentKeys, isContentCleaned } from 'webapp/utils/content-cleaner';

type PropType = {
  heading: string;
  topSmallViewportImage?: Excite.ContentApi.ImageApiModel;
  topLargeViewportImage?: Excite.ContentApi.ImageApiModel;
  shortDescription?: string;
  showCmsContent: boolean;
};

export function ProductListingTop(props: PropType) {
  const isMobile = useMatchMobile();
  const image = isMobile ? props.topSmallViewportImage || props.topLargeViewportImage : props.topLargeViewportImage;
  const showCmsContent = Boolean(image) && props.showCmsContent;
  const elementRef = React.useRef<HTMLParagraphElement>(null);
  const [showReadMoreButton, setShowReadMoreButton] = React.useState<boolean>(false);
  const [showMore, setShowMore] = React.useState<boolean>(false);

  React.useLayoutEffect(() => {
    if (elementRef.current) {
      setShowReadMoreButton(elementRef.current.scrollHeight > elementRef.current.clientHeight);
    }
  }, [setShowReadMoreButton]);

  const readMore = () => {
    setShowMore(true);
    setShowReadMoreButton(false);
  };

  return (
    <Base>
      <Information>
        <h1>{props.heading}</h1>
        {props.showCmsContent && (
          <>
            <Description ref={elementRef} css={!showMore && ellipsisDecorator}>
              {props.shortDescription}
            </Description>
            {showReadMoreButton && (
              <ShowMoreButton
                as={Theme.Transparent}
                onClick={readMore}
                size={isMobile ? FieldSize.Small : FieldSize.Default}
              >
                {translate(t => t.readMore)}
              </ShowMoreButton>
            )}
          </>
        )}
      </Information>
      {showCmsContent && !isContentCleaned(ContentKeys.TopImage) && (
        <ListImage image={image} preset={isMobile ? undefined : 'medium'} sizes={isMobile ? undefined : '40vw'} />
      )}
    </Base>
  );
}

const Base = styled.div(
  maxContentNormalWidthDecorator,
  createMaxMobileViewportDecorator({ padding: { x: 0 } }),
  createMinLargeViewportDecorator({ padding: { x: t => t.spacing.xxxl } }),
  createMinMediumViewportDecorator({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: t => t.spacing.s,
    marginBottom: t => t.spacing.xs,
  }),
  {
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: t => t.spacing.s,
    padding: { x: t => t.spacing.xs },
    marginBottom: t => t.spacing.s,
  },
);

const Information = styled.div(
  createMaxMobileViewportDecorator({
    textAlign: 'center',
    padding: { x: t => t.spacing.xs },
  }),
  createMinMediumViewportDecorator({
    maxWidth: 526,
  }),
);

const ListImage = styled(Image, {
  width: '100%',
  objectFit: 'cover',
});

const Description = styled.p(
  createMaxMobileViewportDecorator({
    fontSize: t => t.fontSize.xxs,
  }),
  {
    marginBottom: 0,
  },
);

const ShowMoreButton = styled(
  Button,
  createMaxMobileViewportDecorator({
    margin: { x: 'auto' },
  }),
  {
    textDecoration: 'underline',
    padding: { xy: 0 },
  },
);

const ellipsisDecorator = styled(
  createMinMediumViewportDecorator({
    WebkitLineClamp: 8,
  }),
  {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
);
